import { useRef, useState } from 'react';
import clsx from 'clsx';
import { Camera, Loader2, X } from 'lucide-react';
import { companiesService } from '@/services/companies.service';
import toast from 'react-hot-toast';

/**
 * Company logo picker. Shows the current logo (or the company's initials),
 * uploads the chosen image to ImageKit through the backend, and hands the
 * hosted URL to the parent via onChange. onChange(undefined) = logo removed.
 */
export function LogoUpload({
  value, name, onChange, size = 72,
}: {
  value?: string;
  name: string;
  onChange: (url: string | undefined) => void | Promise<void>;
  size?: number;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [failedUrl, setFailedUrl] = useState<string>();

  const initials = (name || '?').split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Logo must be an image (PNG, JPG, SVG, WEBP)'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo must be 5 MB or smaller'); return; }
    setBusy(true);
    try {
      const { url } = await companiesService.uploadLogo(file);
      await onChange(url);
    } catch { /* axios interceptor toasts */ } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="group/logo relative shrink-0" style={{ width: size, height: size }}>
      <button
        type="button"
        onClick={() => !busy && fileRef.current?.click()}
        title={value ? 'Change company logo' : 'Add company logo (optional)'}
        className="group relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl border border-border bg-primary/10 transition-colors hover:border-primary/50"
      >
        {value && value !== failedUrl ? (
          <img src={value} alt="" onError={() => setFailedUrl(value)} className="h-full w-full object-cover" />
        ) : (
          <span className="font-bold text-primary" style={{ fontSize: size * 0.3 }}>{initials}</span>
        )}
        {/* Hover / busy overlay */}
        <span className={clsx(
          'absolute inset-0 flex items-center justify-center bg-black/45 text-white transition-opacity',
          busy ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        )}>
          {busy ? <Loader2 size={size * 0.26} className="animate-spin" /> : <Camera size={size * 0.26} />}
        </span>
      </button>
      {/* Always-visible camera badge — signals "this is the logo, click to change" */}
      <span className="pointer-events-none absolute -bottom-1.5 -right-1.5 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-surface text-text-muted shadow-sm">
        <Camera size={13} />
      </span>
      {value && !busy && (
        <button
          type="button"
          onClick={() => onChange(undefined)}
          title="Remove logo"
          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-surface text-text-muted opacity-0 shadow-sm transition-opacity hover:text-danger group-hover/logo:opacity-100"
        >
          <X size={12} />
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => pick(e.target.files?.[0])} />
    </div>
  );
}
